import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'

/** An insurer's NAIC company code. Certificates look the code up by the insurer's name. */
export interface NaicCompany {
  id: string
  naicCode: string
  name: string
}

export interface NaicCompanyInput {
  naicCode: string
  name: string
}

/** The field errors the API returns when a code or name is malformed or already listed. */
export interface NaicCompanyFieldErrors {
  naic_code?: string[]
  name?: string[]
}

const BASE = '/api/v1/insure/internal/naic-companies/'

export function useNaicCompanies() {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.NAIC_COMPANIES],
    queryFn: () => axios.get(BASE).then((response) => toCamelCase(response.data) as NaicCompany[]),
  })
  return { companies: data ?? [], isLoading }
}

/** Adds a company, or edits the one whose id is given. */
export function useSaveNaicCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, naicCode, name }: NaicCompanyInput & { id?: string }) => {
      const body = { naic_code: naicCode, name }
      const request = id ? axios.patch(`${BASE}${id}/`, body) : axios.post(BASE, body)
      return request.then((response) => toCamelCase(response.data) as NaicCompany)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.NAIC_COMPANIES] }),
  })
}
